import { useRef, useReducer, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import {
  Button,
  Form,
  FormControl,
  Input,
  Label,
  Loader,
  TextArea,
} from "@hydra/atom/components";
import { selectPermission } from "@/store/userSlice";
import { CustomActionDropdown } from "@/components/dynamic";

import { BoxedContent, Header, FormLeftHeader } from "@/components/common";
import {
  ledgerAccountCategoryFormReducer,
  initialState,
  setFormValue,
  setInitialState,
} from "@/reducers/finance/ledgerAccountCategoryFormReducer";
import {
  createLedgerAccountCategory,
  updateLedgerAccountCategory,
  getLedgerAccountCategoryById,
  deleteLedgerAccountCategory,
} from "@/api/finance/ledgerAccountCategoryApi";
import showToast from "@/utils/toast/helpers";

const validationMessages = {
  required: {
    required: "This field is required",
  },
};

const prepareData = (state) => state;

const prepareState = (data) => data;

function ActionCell({ deleteMutation, id }) {
  const actions = [
    {
      title: "Delete",
      onClick: () => {
        deleteMutation.mutate(id);
      },
      icon: "delete-icon",
    },
  ];
  return (
    <div className="action-cell">
      <CustomActionDropdown
        actions={actions}
        trigger={(
          <div className="action-dropdown-trigger">
            <span className="text">Actions</span>
            <span className="material-icons-outlined">expand_more</span>
          </div>
        )}
        testId="Add-GL-Action-Button"
      />
    </div>
  );
}

export default function LedgerAccountCategory() {
  const [state, dispatch] = useReducer(
    ledgerAccountCategoryFormReducer,
    initialState
  );
  const deletePermission = useSelector(
    selectPermission({
      parent: "Model",
      scope: "LedgerAccountCategory",
      action: "Delete",
    })
  );

  const submitButtonRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isInitialLoading } = useQuery(
    ["ledger-account-category", id],
    () => getLedgerAccountCategoryById(id),
    {
      enabled: id !== undefined,
    }
  );

  useEffect(() => {
    if (id && data) {
      const stateData = prepareState(data?.data);
      dispatch(setInitialState(stateData));
    }
  }, [data]);

  const saveMutation = useMutation(createLedgerAccountCategory, {
    onError: () => {
      showToast("Could not create. Try again!", "error");
    },
    onSuccess: () => {
      showToast("Created successfully", "success");
      navigate(-1);
    },
  });

  const updateMutation = useMutation(
    ({ id: recordId, data: recordData }) =>
      updateLedgerAccountCategory(recordId, recordData),
    {
      onError: () => {
        showToast("Could not update. Try again!", "error");
      },
      onSuccess: () => {
        showToast("Updated successfully", "success");
        navigate(-1);
      },
    }
  );

  const deleteMutation = useMutation(deleteLedgerAccountCategory, {
    onError: () => {
      showToast("Could not delete. Try again!", "error");
    },
    onSuccess: () => {
      showToast("Deleted successfully", "success");
      QueryClient.invalidateQueries({
        queryKey: ["ledger-account-category"],
      });
    },
  });

  const handleCancel = () => {
    dispatch(setInitialState(initialState));
    navigate(-1);
  };

  const handleSave = () => {
    submitButtonRef.current.click();
  };

  const handleSubmit = async () => {
    const recordData = prepareData(state);

    if (id) {
      updateMutation.mutate({
        id,
        data: recordData,
      });
      return;
    }

    saveMutation.mutate(recordData);
  };

  if (id && isInitialLoading) {
    return <Loader />;
  }

  return (
    <BoxedContent className="gl-account-form">
      <Header
        showBreadcrumb
        leftContent={
          <h1>{`${id ? "Edit" : "Create"} Ledger Account Category`}</h1>
        }
        // rightContent={
        //   id && deletePermission ? (
        //     <ActionCell deleteMutation={deleteMutation} id={id} />
        //   ) : null
        // }
      />
      <Form
        onSubmit={handleSubmit}
        key="currency-form"
        className="dynamic-object-form"
        shouldScrollOnError
      >
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Name & Details"
              subtitle="Provide Name & Details of currency"
              icon="scan-icon"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="name" label="Name*" />
                  <Input
                    name="name"
                    placeholder="Name"
                    value={state.name}
                    onChange={(value) => dispatch(setFormValue("name", value))}
                    rules="required"
                    messages={validationMessages.required}
                    testId="Ledger-Account-Category-Name-Input"
                  />
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="externalNumber" label="External Number*" />
                  <Input
                    placeholder="External Number"
                    value={state.externalNumber}
                    onChange={(value) =>
                      dispatch(setFormValue("externalNumber", value))}
                    rules="required"
                    messages={validationMessages.required}
                    testId="Ledger-Account-Category-ExternalNumber-Input"
                  />
                </FormControl>
              </div>
              <div className="col-md-12">
                <FormControl>
                  <Label htmlFor="description" label="Description" />
                  <TextArea
                    placeholder="Description"
                    value={state.description}
                    onChange={(value) =>
                      dispatch(setFormValue("description", value))}
                    testId="Ledger-Account-Category-Description-Input"
                  />
                </FormControl>
              </div>
            </div>
          </div>
          <button
            type="submit"
            style={{ display: "none" }}
            ref={submitButtonRef}
          >
            Save
          </button>
        </div>
      </Form>
      <div className="col-md-12">
        <div className="hr" />
      </div>

      <div className="col">
        <div className="buttons-at-end">
          <Button
            bordered
            small
            onClick={handleCancel}
            testId="Ledger-Account-Category-Cancel-Button"
          >
            Cancel
          </Button>
          <Button
            small
            className="save-btn"
            loading={saveMutation.isLoading || updateMutation.isLoading}
            onClick={handleSave}
            testId="Ledger-Account-Category-Submit-Button"
          >
            Save
          </Button>
        </div>
      </div>
    </BoxedContent>
  );
}
